import { useEffect, useRef, useState } from 'react';

/**
 * Sticky-bar scroll helpers.
 *
 * The document (body) scrolls, so these hooks listen on `window`. All of them
 * fan in to ONE shared passive scroll listener + ONE resize listener (mounted
 * while at least one subscriber exists) — the docstring promise. Each
 * subscriber keeps its own rAF throttle, so per-hook behavior and cost are
 * unchanged; only the per-instance DOM listener pairs are gone (the discovery
 * classes list used to mount 8-15 of them at once).
 */

type ScrollSubscriber = () => void;

const scrollSubscribers = new Set<ScrollSubscriber>();
let sharedListener: (() => void) | null = null;

function attachSharedListeners() {
  if (sharedListener) return;
  const fanOut = () => scrollSubscribers.forEach((notify) => notify());
  sharedListener = fanOut;
  window.addEventListener('scroll', fanOut, { passive: true, capture: true });
  window.addEventListener('resize', fanOut);
}

function detachSharedListeners() {
  if (!sharedListener || scrollSubscribers.size > 0) return;
  window.removeEventListener('scroll', sharedListener, { capture: true });
  window.removeEventListener('resize', sharedListener);
  sharedListener = null;
}

function useScrollListener(callback: () => void, enabled = true) {
  // Read the latest callback through a ref so it can change between renders
  // without tearing down and re-adding the subscription.
  const cb = useRef(callback);
  cb.current = callback;

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    let ticking = false;
    let frameId: number | null = null;
    const run = () => {
      frameId = null;
      ticking = false;
      if (!active) return;
      cb.current();
    };
    // rAF throttle stays per subscriber so a scroll burst still coalesces into
    // one callback execution per hook per frame.
    const notify = () => {
      if (!active || ticking) return;
      ticking = true;
      frameId = window.requestAnimationFrame(run);
    };
    scrollSubscribers.add(notify);
    attachSharedListeners();
    notify();
    return () => {
      active = false;
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      scrollSubscribers.delete(notify);
      detachSharedListeners();
    };
  }, [enabled]);
}

export function usePrefersReducedMotion(): boolean {
  const [reducedMotion, setReducedMotion] = useState(false);
  const matchesNow =
    typeof window !== 'undefined'
      ? (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false)
      : false;

  useEffect(() => {
    const mediaQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mediaQuery) return;
    const onChange = (event: { matches: boolean }) => setReducedMotion(event.matches);
    setReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  return reducedMotion || matchesNow;
}

/**
 * Resolves the sticky threshold from the element's final CSS geometry. This
 * keeps JS in sync with safe-area-aware `top` values without duplicating them.
 */
export function readStickyTop(element: Element, override?: number): number {
  if (override !== undefined) return override;
  const top = Number.parseFloat(globalThis.getComputedStyle(element).top);
  return Number.isFinite(top) ? top : 0;
}

/**
 * True once `ref` has scrolled to/past `top` px from the viewport top — i.e.
 * the element is pinned. Only updates state when the boolean flips, so it costs
 * one render per pin/unpin transition, not per scroll frame.
 */
export function useStickyPin<T extends Element>(
  ref: React.RefObject<T | null>,
  top?: number
): boolean {
  const [stuck, setStuck] = useState(false);
  useScrollListener(() => {
    const el = ref.current;
    if (!el) return;
    const next = el.getBoundingClientRect().top <= readStickyTop(el, top) + 0.5;
    setStuck((prev) => (prev === next ? prev : next));
  });
  return stuck;
}

/**
 * Scroll-driven morph for a sticky element. Writes a `--pill` custom property
 * (0..1) straight onto the element's inline style — 0 at rest, 1 once pinned —
 * with the value sweeping across `distance` px of scroll *as the element
 * approaches the pin point*, so the morph tracks scroll position instead of a
 * CSS animation. No React state → zero re-renders; the only per-frame cost is
 * one `getBoundingClientRect` (same as `useStickyPin`), and a style write only
 * when the value actually changes. CSS interpolates the visual properties via
 * `calc(... * var(--pill))`.
 */
export function useStickyMorph<T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  offset: number | undefined = undefined,
  distance = 32
): void {
  const last = useRef(-1);
  const reducedMotion = usePrefersReducedMotion();
  useEffect(() => {
    if (!reducedMotion) {
      last.current = -1;
      return;
    }
    last.current = 1000;
    ref.current?.style.setProperty('--pill', '1.000');
  }, [reducedMotion, ref]);
  useScrollListener(() => {
    const el = ref.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    const stickyTop = readStickyTop(el, offset);
    let p = (stickyTop + distance - top) / distance;
    if (p < 0) p = 0;
    else if (p > 1) p = 1;
    // toFixed(3) lets us skip writes for sub-pixel jitter (most frames for most
    // headers are far from the pin window and bail here).
    const key = Math.round(p * 1000);
    if (key === last.current) return;
    last.current = key;
    el.style.setProperty('--pill', p.toFixed(3));
  }, !reducedMotion);
}

/**
 * True once the scroll container has moved past `threshold` px. Used by the
 * fixed app header, which is always pinned — so "pin" is meaningless for it and
 * we watch the scroll offset directly instead.
 */
export function useScrolled(threshold = 0.5): boolean {
  const [scrolled, setScrolled] = useState(false);
  useScrollListener(() => {
    const next = window.scrollY > threshold;
    setScrolled((prev) => (prev === next ? prev : next));
  });
  return scrolled;
}
